import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Typography,
  Tag,
  Select,
  Input,
  DatePicker,
  Modal,
  notification,
  Badge,
  InputNumber,
  Form,
  Alert,
  Row,
  Col,
  Breadcrumb,
  Tooltip,
} from "antd";
import {
  FaInfoCircle,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaSyncAlt,
  FaPercent,
} from "react-icons/fa";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import moment from "moment";
import { debounce } from "lodash";
import "./RoomPricePage.css";

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { confirm } = Modal;

const money = (currency, value) =>
  `${currency || "$"}${(value ?? 0).toFixed(2)}`;

const RoomPricePage = () => {
  const { authData } = useAuth();
  const [roomTypes, setRoomTypes] = useState([]);
  const [prices, setPrices] = useState([]);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [activeDiscounts, setActiveDiscounts] = useState([]);

  useEffect(() => {
    if (authData?.hotelId) {
      fetchRoomTypes();
      debouncedFetchPrices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.hotelId]);

  useEffect(() => {
    const interval = setInterval(checkDiscountExpiry, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prices]);

  const checkDiscountExpiry = () => {
    const now = moment();
    const expiredDiscounts = prices.filter(
      (price) =>
        price.discount > 0 &&
        price.discountEnd &&
        moment(price.discountEnd).isBefore(now)
    );

    if (expiredDiscounts.length > 0) {
      expiredDiscounts.forEach((price) => {
        handleDiscountExpiry(price.roomPriceId);
      });
    }
  };

  const handleDiscountExpiry = async (priceId) => {
    try {
      await axiosInstance.put(`/Price/${priceId}/expire-discount`);
      fetchPrices();
      notification.info({
        message: "Discount Expired",
        description: "A scheduled discount has been automatically reset",
      });
    } catch (error) {
      console.error("Error expiring discount:", error);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const response = await axiosInstance.get(
        `/RoomTypes/GetRoomTypes/${authData.hotelId}`
      );
      setRoomTypes(response.data || []);
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch room types",
      });
    }
  };

  const debouncedFetchPrices = debounce(async () => {
    try {
      setFetchingPrices(true);
      const response = await axiosInstance.get(
        `/Price/hotel/${authData.hotelId}`
      );
      const pricesData = response.data || [];
      setPrices(pricesData);

      const now = moment();
      const active = pricesData.filter(
        (price) =>
          price.discount > 0 &&
          price.discountStart &&
          price.discountEnd &&
          moment(price.discountStart).isBefore(now) &&
          moment(price.discountEnd).isAfter(now)
      );
      setActiveDiscounts(active);
    } catch (error) {
      if (error.response?.status !== 404) {
        notification.error({
          message: "Error",
          description: "Failed to fetch prices",
        });
      }
      setPrices([]);
    } finally {
      setFetchingPrices(false);
    }
  }, 300);

  const fetchPrices = () => {
    debouncedFetchPrices();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const priceData = {
        ...values,
        hotelId: authData.hotelId,
        roomTypeId: parseInt(values.roomTypeId),
        discount: values.discount || 0,
        additionalLevies: values.additionalLevies || 0, // absolute amount
        discountStart: values.discountPeriod?.[0]?.toISOString(),
        discountEnd: values.discountPeriod?.[1]?.toISOString(),
      };

      if (editingPriceId) {
        await axiosInstance.put(`/Price/${editingPriceId}`, priceData);
        notification.success({
          message: "Success",
          description: "Price updated successfully",
        });
      } else {
        await axiosInstance.post("/Price", priceData);
        notification.success({
          message: "Success",
          description: "Price created successfully",
        });
      }

      form.resetFields();
      setEditingPriceId(null);
      fetchPrices();
    } catch (error) {
      console.error("Error saving price:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (price) => {
    form.setFieldsValue({
      ...price,
      roomTypeId: price.roomTypeId.toString(),
      discountPeriod: [
        price.discountStart ? moment(price.discountStart) : null,
        price.discountEnd ? moment(price.discountEnd) : null,
      ],
    });
    setEditingPriceId(price.roomPriceId);
  };

  const handleDelete = (priceId) => {
    confirm({
      title: "Delete Price",
      content: "Are you sure you want to delete this price?",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await axiosInstance.delete(`/Price/${priceId}`);
          notification.success({
            message: "Success",
            description: "Price deleted successfully",
          });
          fetchPrices();
        } catch (error) {
          notification.error({
            message: "Error",
            description: "Failed to delete price",
          });
        }
      },
    });
  };

  const getRoomTypeName = (roomTypeId) => {
    const roomType = roomTypes.find((rt) => rt.roomTypeId === roomTypeId);
    return roomType?.typeName || "Unknown";
  };

  const getDiscountStatus = (price) => {
    if (!price.discount || price.discount <= 0) return null;

    const now = moment();
    const start = moment(price.discountStart);
    const end = moment(price.discountEnd);

    if (!start || !end) return <Tag color="orange">Invalid Period</Tag>;
    if (now.isBefore(start)) return <Tag color="blue">Scheduled</Tag>;
    if (now.isAfter(end)) return <Tag color="red">Expired</Tag>;
    return <Tag color="green">Active</Tag>;
  };

  // --- Price math helpers (per-night) ---
  const discounted = (base, discPct) => base * (1 - (discPct || 0) / 100);
  const vatAmount = (net, vatPct) => net * ((vatPct || 0) / 100);
  // additionalLevies is an ABSOLUTE currency amount per night

  const columns = [
    {
      title: "Room Type",
      dataIndex: "roomTypeId",
      key: "roomType",
      fixed: "left",
      render: (id) => <Text strong>{getRoomTypeName(id)}</Text>,
    },
    {
      title: "Base Price",
      dataIndex: "basePrice",
      key: "basePrice",
      render: (price, record) => money(record.currency, price),
    },
    {
      title: "Discount %",
      dataIndex: "discount",
      key: "discount",
      render: (discount, record) => (
        <Space>
          <Tag color={discount > 0 ? "green" : "default"}>
            {discount ? `${discount}%` : "None"}
          </Tag>
          {getDiscountStatus(record)}
        </Space>
      ),
    },
    {
      title: "VAT %",
      dataIndex: "vatPercentage",
      key: "vatPercentage",
      render: (vat) => `${vat || 0}%`,
    },
    {
      title: "Levies",
      dataIndex: "additionalLevies",
      key: "additionalLevies",
      render: (levy, record) => money(record.currency, levy || 0),
    },
    {
      title: "Net (after discount)",
      key: "netAfterDiscount",
      render: (_, r) =>
        money(r.currency, discounted(r.basePrice || 0, r.discount || 0)),
    },
    {
      title: "VAT Amt",
      key: "vatAmount",
      render: (_, r) => {
        const net = discounted(r.basePrice || 0, r.discount || 0);
        const vat = vatAmount(net, r.vatPercentage || 0);
        return money(r.currency, vat);
      },
    },
    {
      title: (
        <Tooltip title="Net after discount + VAT amount + Levies">
          Total / night
        </Tooltip>
      ),
      key: "totalPerNight",
      render: (_, r) => {
        const net = discounted(r.basePrice || 0, r.discount || 0);
        const vat = vatAmount(net, r.vatPercentage || 0);
        const total = net + vat + (r.additionalLevies || 0);
        return (
          <Text strong style={{ whiteSpace: "nowrap" }}>
            {money(r.currency, total)}
          </Text>
        );
      },
    },
    {
      title: "Discount Period",
      key: "discountPeriod",
      render: (_, record) =>
        record.discountStart && record.discountEnd ? (
          <Text>
            {moment(record.discountStart).format("MMM D")} -{" "}
            {moment(record.discountEnd).format("MMM D, YYYY")}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button icon={<FaEdit />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button
            danger
            icon={<FaTrash />}
            onClick={() => handleDelete(record.roomPriceId)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb style={{ marginBottom: 12 }}>
        <Breadcrumb.Item>
          <Link to="/">Home</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Pricing</Breadcrumb.Item>
        <Breadcrumb.Item>Room Prices</Breadcrumb.Item>
      </Breadcrumb>

      <Card
        title={
          <Space>
            <Text strong>Room Prices</Text>
            <Badge count={prices.length} showZero style={{ backgroundColor: "#1890ff" }} />
            {activeDiscounts.length > 0 && (
              <Badge
                count={`${activeDiscounts.length} Active Discounts`}
                style={{ backgroundColor: "#52c41a" }}
              />
            )}
            <Button
              type="text"
              icon={<FaInfoCircle />}
              onClick={() => setShowInfoModal(true)}
            />
          </Space>
        }
        extra={
          <Button icon={<FaSyncAlt />} onClick={fetchPrices} loading={fetchingPrices}>
            Refresh
          </Button>
        }
      >
        {roomTypes.length === 0 ? (
          <Alert
            type="warning"
            message={
              <span>
                Before setting prices, please{" "}
                <Link to="/hotels/roomtypespage">create room types</Link>
              </span>
            }
            showIcon
          />
        ) : (
          <>
            <Form form={form} layout="vertical" onFinish={handleSubmit} className="price-form">
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="roomTypeId"
                    label="Room Type"
                    rules={[{ required: true, message: "Please select a room type" }]}
                  >
                    <Select placeholder="Select room type">
                      {roomTypes.map((type) => (
                        <Option key={type.roomTypeId} value={type.roomTypeId.toString()}>
                          {type.typeName}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="basePrice"
                    label="Base Price"
                    rules={[{ required: true, message: "Please enter base price" }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      step={0.01}
                      formatter={(value) => ` ${form.getFieldValue("currency") || "$"} ${value}`}
                      parser={(value) => value.replace(/[^0-9.]/g, "")}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name="currency"
                    label="Currency"
                    initialValue="BWP"
                    rules={[{ required: true, message: "Please select currency" }]}
                  >
                    <Select>
                      <Option value="BWP">BWP (Pula)</Option>
                      <Option value="USD">USD ($)</Option>
                      <Option value="EUR">EUR (€)</Option>
                      <Option value="GBP">GBP (£)</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    name="vatPercentage"
                    label="VAT %"
                    rules={[{ required: true, message: "Please enter VAT percentage" }]}
                  >
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      step={0.01}
                      formatter={(value) => `${value}%`}
                      parser={(value) => value.replace("%", "")}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="discount" label="Discount" tooltip="Enter 0 to remove discount">
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      max={100}
                      step={0.01}
                      prefix={<FaPercent />}
                      formatter={(value) => `${value}%`}
                      parser={(value) => value.replace("%", "")}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="additionalLevies" label="Additional Levies" initialValue={0}>
                    <InputNumber
                      style={{ width: "100%" }}
                      min={0}
                      step={0.01}
                      formatter={(value) => ` ${form.getFieldValue("currency") || "$"} ${value}`}
                      parser={(value) => value.replace(/[^0-9.]/g, "")}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="discountPeriod"
                label="Discount Validity Period"
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (
                        getFieldValue("discount") > 0 &&
                        (!value || !value[0] || !value[1])
                      ) {
                        return Promise.reject(new Error("Please select discount period"));
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <RangePicker
                  style={{ width: "100%" }}
                  disabledDate={(current) => current && current < moment().startOf("day")}
                />
              </Form.Item>

              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={loading} icon={<FaSave />}>
                    {editingPriceId ? "Update" : "Save"}
                  </Button>
                  {editingPriceId && (
                    <Button
                      onClick={() => {
                        form.resetFields();
                        setEditingPriceId(null);
                      }}
                      icon={<FaTimes />}
                    >
                      Cancel
                    </Button>
                  )}
                </Space>
              </Form.Item>
            </Form>

            <Table
              columns={columns}
              dataSource={prices}
              rowKey="roomPriceId"
              pagination={{ pageSize: 10 }}
              scroll={{ x: true }}
              loading={fetchingPrices}
              locale={{
                emptyText: prices.length === 0 ? "No prices found" : undefined,
              }}
            />
          </>
        )}

        <Modal
          title="Room Pricing Guide"
          open={showInfoModal}
          onCancel={() => setShowInfoModal(false)}
          footer={[
            <Button key="close" onClick={() => setShowInfoModal(false)}>
              Close
            </Button>,
          ]}
        >
          <p>
            This module allows you to set prices for your room types with time-limited discounts.
          </p>
          <h4>Key Features:</h4>
          <ul>
            <li>Set base prices with VAT and additional charges</li>
            <li>Create time-limited discounts with automatic expiration</li>
            <li>Visual indicators for active/scheduled/expired discounts</li>
            <li>Multiple currency support</li>
          </ul>
          <h4>Discount Management:</h4>
          <ul>
            <li>Discounts automatically expire at the end of their validity period</li>
            <li>The system checks for expired discounts every minute</li>
            <li>Active discounts are highlighted in the overview</li>
          </ul>
        </Modal>
      </Card>
    </div>
  );
};

export default RoomPricePage;
